// src/pages/MyBookings.jsx
import { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/base";
import { useAuth } from "../context/AuthContext";
import BookingCard from "../components/BookingCard";
import { formatSalonLabel } from "../utils/formatSalonLabel";

const API = API_BASE;

// ---------- Pure helpers (outside component, no hook deps) ----------
const isBookingCancelled = (b) =>
  (b.status || "").toLowerCase() === "cancelled";

const parseBookingDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const isBookingPast = (b) => {
  const now = new Date();
  const end = parseBookingDate(b.end_time);
  const start = parseBookingDate(b.start_time);

  if (end) return end < now;
  if (start) return start < now;
  return false;
};

// Extract a human-readable error message from FastAPI / Pydantic errors
const extractErrorMessage = (
  payload,
  fallback = "Failed to load bookings."
) => {
  if (!payload) return fallback;

  // If backend already sent a simple string
  if (typeof payload === "string") return payload;

  // FastAPI validation errors: { detail: [ { msg, loc, type }, ... ] }
  if (Array.isArray(payload.detail)) {
    const first = payload.detail[0];
    if (!first) return fallback;

    if (typeof first === "string") return first;
    if (typeof first.msg === "string") return first.msg;

    // Last resort: stringify
    try {
      return JSON.stringify(payload.detail);
    } catch {
      return fallback;
    }
  }

  // { detail: "some message" }
  if (typeof payload.detail === "string") return payload.detail;

  // { message: "some message" }
  if (typeof payload.message === "string") return payload.message;

  // Anything else – stringify the payload
  try {
    return JSON.stringify(payload);
  } catch {
    return fallback;
  }
};

// -----------------------------------------------------------

export default function MyBookings() {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedSalon, setSelectedSalon] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("all"); // all | confirmed | cancelled

  // For admin we were building a set of "my" ids; BookingCard now uses only
  // is_mine / created_by_user_id, but we keep this in case you want it later.
  const [adminMyBookingIds, setAdminMyBookingIds] = useState(new Set());

  const role = (localStorage.getItem("role") || "").toLowerCase();
  const currentUserId = Number(localStorage.getItem("user_id") || "0");

  // -----------------------------
  // Helpers to normalize data (for filters)
  // -----------------------------
const getSalonName = (b) =>
  b.salon_name ||
  b.salon?.name ||
  b.booth?.salon?.name ||
  b.booth?.salon_name ||
  "Unknown salon";

const getSalonId = (b) =>
  b.salon_id ??
  b.salon?.id ??
  b.booth?.salon?.id ??
  b.booth?.salon_id ??
  null;

  const getBookingId = (b) => b.booking_id ?? b.id;

  // -----------------------------
  // Fetch bookings
  // -----------------------------
  useEffect(() => {
    if (!token) {
      setError("You are not logged in. Please log in again.");
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API}/bookings/my`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          const detail = extractErrorMessage(
            payload,
            res.statusText || "Failed to load bookings."
          );
          throw new Error(detail);
        }

        const data = await res.json();
        setBookings(Array.isArray(data) ? data : []);
        setAdminMyBookingIds(new Set());
      } catch (e) {
        console.error("Load bookings failed:", e);
        const msg =
          typeof e?.message === "string" && e.message.length > 0
            ? e.message
            : "Failed to load bookings.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [role, token]);

  // -----------------------------
  // Cancel Booking (soft-cancel → keeps in history)
  // -----------------------------
  const handleCancel = async (booking) => {
    const id = getBookingId(booking);
    if (!id) {
      alert("Cannot cancel: missing booking id.");
      return;
    }

    if (!window.confirm("Are you sure you want to cancel this booking?")) {
      return;
    }

    try {
      const res = await fetch(`${API}/bookings/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        const detail = extractErrorMessage(
          payload,
          res.statusText || "Failed to cancel booking."
        );
        throw new Error(detail);
      }

      setBookings((prev) => prev.filter((b) => getBookingId(b) !== id));
    } catch (e) {
      console.error("Cancel booking failed:", e);
      const msg =
        typeof e?.message === "string" && e.message.length > 0
          ? e.message
          : "Unable to cancel booking.";
      alert(msg);
    }
  };

  // -----------------------------
  // Salon filter (admin only)
  // -----------------------------
  const salonOptions = useMemo(() => {
    if (role !== "admin") return [];
    const map = new Map();
    bookings.forEach((booking) => {
      const name = getSalonName(booking);
      if (!map.has(name)) {
        map.set(name, getSalonId(booking));
      }
    });
    return Array.from(map.entries())
      .map(([name, id]) => ({ name, id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [bookings, role]);

  const bookingsAfterSalonFilter = useMemo(() => {
    if (role !== "admin" || selectedSalon === "ALL") return bookings;
    return bookings.filter((b) => getSalonName(b) === selectedSalon);
  }, [bookings, role, selectedSalon]);

  // -----------------------------
  // Status filter (all / confirmed / cancelled)
  // -----------------------------
  const bookingsAfterStatusFilter = useMemo(() => {
    if (statusFilter === "confirmed") {
      return bookingsAfterSalonFilter.filter((b) => !isBookingCancelled(b));
    }
    if (statusFilter === "cancelled") {
      return bookingsAfterSalonFilter.filter(isBookingCancelled);
    }
    return bookingsAfterSalonFilter;
  }, [bookingsAfterSalonFilter, statusFilter]);

  // -----------------------------
  // Time buckets: Upcoming vs History
  // Upcoming = NOT cancelled AND NOT past
  // History  = cancelled OR past
  // -----------------------------
  const upcomingBookings = useMemo(
    () =>
      bookingsAfterStatusFilter.filter(
        (b) => !isBookingCancelled(b) && !isBookingPast(b)
      ),
    [bookingsAfterStatusFilter]
  );

  const historyBookings = useMemo(
    () =>
      bookingsAfterStatusFilter.filter(
        (b) => isBookingCancelled(b) || isBookingPast(b)
      ),
    [bookingsAfterStatusFilter]
  );

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <h1 className="text-3xl font-bold mb-4">My Bookings</h1>

      {/* Filters row */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        {role === "admin" && (
          <div className="flex items-center gap-2">
            <label className="font-medium">Filter by salon:</label>
            <select
              value={selectedSalon}
              onChange={(e) => setSelectedSalon(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="ALL">All salons</option>
              {salonOptions.map((option) => (
                <option
                  key={`${option.name}-${option.id ?? "unknown"}`}
                  value={option.name}
                >
                  {formatSalonLabel(option)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex items-center gap-2">
          <label className="font-medium">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded px-3 py-2"
          >
            <option value="all">All</option>
            <option value="confirmed">Confirmed only</option>
            <option value="cancelled">Cancelled only</option>
          </select>
        </div>
      </div>

      {loading && <p>Loading bookings…</p>}
      {error && !loading && (
        <p className="text-red-600 mb-4 text-sm">{error}</p>
      )}

      {!loading &&
        !error &&
        upcomingBookings.length === 0 &&
        historyBookings.length === 0 && (
          <p>No bookings found.</p>
        )}

      {/* Upcoming section */}
      {(statusFilter === "all" || statusFilter === "confirmed") &&
        upcomingBookings.length > 0 && (
          <>
            <h2 className="text-xl font-semibold mb-3">
              Upcoming Bookings
            </h2>
            <div className="space-y-4 mb-8">
              {upcomingBookings.map((b) => (
                <BookingCard
                  key={getBookingId(b)}
                  booking={b}
                  role={role}
                  currentUserId={currentUserId}
                  adminMyBookingIds={adminMyBookingIds}
                  showCancelButton={!isBookingCancelled(b)}
                  onCancel={() => handleCancel(b)}
                />
              ))}
            </div>
          </>
        )}

      {/* History section: past & cancelled */}
      {historyBookings.length > 0 && (
        <>
          <h2 className="text-xl font-semibold mb-3">
            History (Past &amp; Cancelled)
          </h2>
          <div className="space-y-4">
            {historyBookings.map((b) => (
              <BookingCard
                key={getBookingId(b)}
                booking={b}
                role={role}
                currentUserId={currentUserId}
                adminMyBookingIds={adminMyBookingIds}
                showCancelButton={false}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
